import { useState, useEffect, ChangeEvent, KeyboardEvent, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IProsecutorOrganisationChild } from 'APIPackage/CatalogsModule/interfaces/IProsecutorOrganisationChild';
import {
  getUserProfileIsTest,
  getUserRoleIsProsecutorOrSuperProsecutor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectOrganisations } from 'UIPackage/SelectOrganisationsModule/components/SelectOrganisations';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './RelatedOrganisationsModal.module.scss';
import {
  userInfoProsecutorOrganizationsGet,
  userInfoProsecutorOrganizationsNextPageGet,
  userInfoProsecutorOrganizationsReset,
  userInfoProsecutorOrganizationsSetParentId,
  userInfoProsecutorOrganizationsSetSearchOgrn,
  userInfoProsecutorOrganizationsSetSearchQuery,
} from '../actions/userInfoActions';
import {
  getUserInfoProsecutorOrganizationsData,
  getUserInfoProsecutorOrganizationsSearchQuery,
  getUserInfoProsecutorOrganizationsParentId,
  getUserInfoProsecutorOrganizationsTotalCount,
} from '../selectors/userInfoSelectors';
import { mainProsecutorOrganizationDistrictId } from '../constants/mainProsecutorOrganizationDistrictId';

const SEARCH_INPUT_ERROR = 'Поле поиска должно содержать не менее 4 символов';

interface IRelatedOrganisationsModalProps {
  visible: boolean;
  selectedOrganisations: IUserOrganisation[];
  onApply: (organisations: IProsecutorOrganisationChild[]) => void;
  onClose: () => void;
  isEsiaAccount: boolean;
}

interface IRelatedOrganisationsModalStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  prosecutorOrganisations: IProsecutorOrganisationChild[];
  searchQueryRequest: string | undefined;
  parentId: string | undefined;
  totalCount: number;
  isTestUser: boolean;
}

const RelatedOrganisationsModalWithoutStore = ({
  visible,
  selectedOrganisations,
  onApply,
  onClose,
  isProsecutorOrSuperProsecutor,
  parentId,
  prosecutorOrganisations,
  searchQueryRequest,
  totalCount,
  isTestUser,
  isEsiaAccount,
}: IRelatedOrganisationsModalProps & IRelatedOrganisationsModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    if (visible) {
      if (isEsiaAccount) {
        if (selectedOrganisations && selectedOrganisations.length > 0) {
          const { ogrn } = selectedOrganisations[0];

          setSearchInputError('');
          dispatch(userInfoProsecutorOrganizationsSetParentId(undefined));
          dispatch(userInfoProsecutorOrganizationsSetSearchOgrn(ogrn));
          dispatch(userInfoProsecutorOrganizationsGet());
          setSearchInputValue(`Выполнен поиск по ОГРН  ${ogrn}`);
        }
      } else {
        dispatch(userInfoProsecutorOrganizationsGet());
      }
    }
  }, [visible, isProsecutorOrSuperProsecutor, dispatch]);

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchInputError, setSearchInputError] = useState('');

  const [selectedOrganisationsList, setSelectedOrganisationsList] = useState<Array<IProsecutorOrganisationChild>>(
    selectedOrganisations,
  );

  useEffect(() => {
    setSelectedOrganisationsList(selectedOrganisations);
  }, [selectedOrganisations]);

  const [currentSelectedOrganisationsList, setCurrentSelectedOrganisationsList] = useState<
    Array<IProsecutorOrganisationChild>
  >([]);

  const disableSelectedId = useMemo(
    () =>
      isProsecutorOrSuperProsecutor
        ? currentSelectedOrganisationsList.find(item =>
            selectedOrganisations.find(
              organization =>
                organization.id === item.id && organization.districtId === mainProsecutorOrganizationDistrictId,
            ),
          )?.id
        : undefined,
    [currentSelectedOrganisationsList, selectedOrganisations, isProsecutorOrSuperProsecutor],
  );

  useEffect(() => {
    setCurrentSelectedOrganisationsList(selectedOrganisationsList);
  }, [selectedOrganisationsList]);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchInputValue(e.target.value),
    [],
  );

  const trySearch = useCallback(() => {
    if (searchInputValue.length === 0 || searchInputValue.length > 3) {
      if (searchQueryRequest !== searchInputValue) {
        setSearchInputError('');
        dispatch(userInfoProsecutorOrganizationsSetParentId(undefined));
        dispatch(userInfoProsecutorOrganizationsSetSearchQuery(searchInputValue));
        dispatch(userInfoProsecutorOrganizationsGet());
      }
    } else {
      setSearchInputError(SEARCH_INPUT_ERROR);
    }
  }, [dispatch, searchInputValue, searchQueryRequest]);

  const handleTextInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        trySearch();
      }
    },
    [trySearch],
  );

  const handleSaveButtonClick = useCallback(() => {
    onApply(currentSelectedOrganisationsList);
  }, [currentSelectedOrganisationsList, onApply]);

  const handleAfterClose = useCallback(() => {
    setSearchInputValue('');
    setSearchInputError('');
    dispatch(userInfoProsecutorOrganizationsReset());
    setCurrentSelectedOrganisationsList(selectedOrganisationsList);
  }, [dispatch, selectedOrganisationsList]);

  const handleSelectedOrganisationsChange = useCallback(
    (organisations: IProsecutorOrganisationChild[]) => setCurrentSelectedOrganisationsList(organisations),
    [],
  );

  const handleOrganisationsParentIdChange = useCallback(
    (id?: string) => {
      dispatch(userInfoProsecutorOrganizationsSetParentId(id));
      dispatch(userInfoProsecutorOrganizationsGet());
    },
    [dispatch],
  );

  const handleOrganisationsPageLoad = useCallback(() => {
    if (prosecutorOrganisations.length < totalCount) {
      dispatch(userInfoProsecutorOrganizationsNextPageGet());
    }
  }, [dispatch, prosecutorOrganisations.length, totalCount]);

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader>
        <h4 className={styles.Title}>Поиск связанных организаций</h4>
        {isProsecutorOrSuperProsecutor && !isTestUser && (
          <p className={styles.Warning}>
            Для начала работы необходимо выбрать прокуратуру. Вы можете указать только одну прокуратуру.
            <br />
            Выбранная прокуратура не подлежит изменению.
          </p>
        )}
      </ModalHeader>
      <ModalBody size="content" border="bottom">
        <TextInput
          className={styles.SearchInput}
          id="keyWord"
          value={searchInputValue}
          placeholder="Ключевое слово"
          error={searchInputError}
          onChange={handleSearchInputChange}
          onKeyDown={handleTextInputKeyDown}
          onBlur={trySearch}
          disabled={isEsiaAccount}
        />
        <SelectOrganisations
          isNavigationVisible={!searchQueryRequest?.length}
          isNavigationRoot={!parentId}
          rootNavigation="НАЧАЛО"
          organisationsTitle="Список доступных связанных организаций"
          selectedOrganisationsTitle="Список выбранных связанных организаций"
          organisations={prosecutorOrganisations}
          isOrganisationsExpanding={!searchQueryRequest?.length}
          isMultiple={!!disableSelectedId}
          selectedOrganisations={currentSelectedOrganisationsList}
          onSelected={handleSelectedOrganisationsChange}
          onOrganisationsParentIdChange={handleOrganisationsParentIdChange}
          onPageLoad={handleOrganisationsPageLoad}
          disableSelectedId={disableSelectedId}
          isLoadButtonVisible={prosecutorOrganisations.length < totalCount}
        />
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button text="Сохранить" color="primary" id="saveButton" size="medium" onClick={handleSaveButtonClick} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IRelatedOrganisationsModalStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  prosecutorOrganisations: getUserInfoProsecutorOrganizationsData(state),
  searchQueryRequest: getUserInfoProsecutorOrganizationsSearchQuery(state),
  parentId: getUserInfoProsecutorOrganizationsParentId(state),
  totalCount: getUserInfoProsecutorOrganizationsTotalCount(state),
  isTestUser: getUserProfileIsTest(state),
});

export const RelatedOrganisationsModal = connect(mapStateToProps)(RelatedOrganisationsModalWithoutStore);
